import { useEffect, useMemo, useRef, useState } from "react";
import { useServices } from "../data/services";
import { useAsync } from "../data/useAsync";
import { haptic, initData, openTelegram, telegramUser } from "../platform/telegram";
import { useNav } from "../nav/router";
import { getFroshBot, inPayoffMode, takeKickoff } from "../froshbot";
import { runDataTool } from "../froshbot/toolRunner";
import "./froshbot.css";

/**
 * FroshBot — the chat, per "03 FroshBot".
 *
 * Voice lives server-side; this screen owns the transcript, the starter
 * prompts, the thinking/error states, and the tool loop: data getters execute
 * against the in-bundle services and round-trip as [RESULT:] turns (live) or
 * render as data slips (mock); navigate offers become chips.
 */

interface NavOffer {
  label: string;
  destination: string;
}

interface UiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  offers?: NavOffer[];
  /** A data slip rather than prose — mock path only. */
  slip?: boolean;
  /** An error bubble; content is the message, retry re-sends this text. */
  retry?: string;
}

/** Demo starters lean on the mock data; real (tma) users have no courses yet,
 * so their starters must not presume any (FOLLOWUPS 8.10). */
const DEMO_STARTERS = [
  "What's due this week?",
  "Am I on track in MATH 1B?",
  "I'm nervous about my roommate",
];

const REAL_STARTERS = [
  "Help me figure out my week",
  "What can you actually do?",
  "I'm nervous about my roommate",
];

let uiSeq = 0;
const uid = () => `ui-${++uiSeq}-${Date.now()}`;

export function FroshBotScreen() {
  const services = useServices();
  const nav = useNav();
  const engine = useMemo(() => getFroshBot(services), [services]);
  // Real identity: the signed launch payload exists, the session runs as
  // `telegram:<id>`, and nothing from the mock data layer may enter the chat.
  const real = Boolean(initData());

  const user = useAsync(() => services.auth.getCurrentUser(), []);
  // The mock auth user (Alex Chen) must never name a real person — inside
  // Telegram the greeting uses the account's own first name (8.10).
  const firstName = real
    ? (telegramUser()?.first_name ?? "")
    : (user.data?.displayName.split(" ")[0] ?? "");

  const [messages, setMessages] = useState<UiMessage[]>(() =>
    engine.history
      .filter((m) => !m.hidden)
      .map((m) => ({ id: m.id, role: m.role, content: m.content })),
  );
  const [thinking, setThinking] = useState(false);
  const [draft, setDraft] = useState("");

  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, thinking]);

  const append = (message: UiMessage) => setMessages((ms) => [...ms, message]);

  const runTurn = async (content: string, hidden: boolean, depth: number): Promise<void> => {
    if (depth === 0) {
      // Clear any earlier error bubble's retry affordance — one at a time.
      setMessages((ms) => ms.filter((m) => m.retry === undefined));
      if (!hidden) append({ id: uid(), role: "user", content });
      setThinking(true);
    }

    const collected = { text: "", offers: [] as NavOffer[] };
    const pendingTools: { name: string; params: Record<string, string> }[] = [];
    let failed: string | null = null;

    await engine.sendTurn(content, { hidden }, (event) => {
      if (event.kind === "text") {
        collected.text += event.text;
      } else if (event.kind === "tool") {
        if (event.name === "navigate") {
          collected.offers.push({
            label: event.params.label ?? "Open",
            destination: event.params.destination ?? "openHome",
          });
        } else {
          pendingTools.push({ name: event.name, params: event.params });
        }
      } else {
        failed = event.message;
      }
    });

    const text = collected.text.trim();
    if (text.length > 0 || collected.offers.length > 0) {
      append({
        id: uid(),
        role: "assistant",
        content: text,
        offers: collected.offers.length > 0 ? collected.offers : undefined,
      });
    }

    if (failed !== null) {
      append({
        id: uid(),
        role: "assistant",
        content: failed,
        retry: hidden ? undefined : content,
      });
      haptic.error();
    } else {
      // Data lookups round-trip so the reply can use the numbers ("a 71 keeps
      // your B"); depth-capped so a confused model can't loop the socket.
      // Real (tma) sessions skip the loop entirely — the getters read the demo
      // mocks, and mock facts must never reach a real user (8.10).
      for (const tool of pendingTools) {
        if (real) continue;
        const result = await runDataTool(tool.name, services);
        if (result === null) continue;
        if (engine.live && depth < 2) {
          await runTurn(`[RESULT: ${tool.name}: ${result}]`, true, depth + 1);
        } else if (!engine.live) {
          append({ id: uid(), role: "assistant", content: result, slip: true });
        }
      }
      if (depth === 0 && text.length > 0) haptic.success();
    }

    if (depth === 0) setThinking(false);
  };

  const send = (raw: string) => {
    const content = raw.trim();
    if (content === "" || thinking) return;
    setDraft("");
    haptic.tap();
    void runTurn(content, false, 0);
  };

  // Onboarding payoff: a hidden kickoff turn armed by the flow, so FroshBot
  // opens the conversation using what the student just entered.
  const kickoffFired = useRef(false);
  useEffect(() => {
    if (kickoffFired.current) return;
    kickoffFired.current = true;
    const kickoff = takeKickoff();
    if (kickoff) void runTurn(kickoff, true, 0);
  }, []);

  const openDestination = (destination: string) => {
    haptic.tap();
    nav.popToRoot();
    switch (destination) {
      case "openStudy":
        nav.setTab("study");
        break;
      case "openConnect":
        nav.setTab("connect");
        break;
      case "openMatchesFeed":
        nav.setTab("connect");
        nav.setConnectSection("matches");
        break;
      case "openProfile":
        nav.setTab("profile");
        break;
      case "openFroshPoints":
        nav.setTab("profile");
        nav.push({ name: "rewards" });
        break;
      case "openEarn":
        nav.setTab("home");
        nav.setHomeSection("earn");
        break;
      case "openCompete":
        nav.setTab("home");
        nav.setHomeSection("compete");
        break;
      case "openCalendar":
        nav.setTab("home");
        nav.push({ name: "calendar" });
        break;
      default:
        nav.setTab("home");
    }
  };

  return (
    <div className="chat">
      <div className="chat__head">
        <span className="rail__mark">
          <img src="/brand/f-white-bold.png" alt="" />
        </span>
        <span className="chat__title">Froshbot</span>
      </div>

      <div className="chat__scroll" ref={scroller}>
        {messages.length === 0 ? (
          <div className="chat__welcome">
            <div className="t-title">Hey {firstName || "there"}. I'm Froshbot.</div>
            <div className="chat__welcome-sub">
              {real
                ? "I remember everything you tell me — here and in your Telegram DMs. No judgment, ever."
                : "I know your courses, your deadlines, and what college actually feels like. No judgment, ever."}
            </div>
            <div className="chat__starters">
              {(real ? REAL_STARTERS : DEMO_STARTERS).map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  className="chat__starter"
                  onClick={() => send(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {messages.map((m) => (
          <div key={m.id} className={`bubble-row bubble-row--${m.role}`}>
            <div
              className={`bubble bubble--${m.role}${m.slip ? " bubble--slip" : ""}${m.retry !== undefined ? " bubble--error" : ""}`}
            >
              {m.content}
              {m.offers ? (
                <div className="bubble__offers">
                  {m.offers.map((offer) => (
                    <button
                      key={offer.destination + offer.label}
                      type="button"
                      className="bubble__offer"
                      onClick={() => openDestination(offer.destination)}
                    >
                      {offer.label}
                    </button>
                  ))}
                </div>
              ) : null}
              {m.retry !== undefined ? (
                <button
                  type="button"
                  className="bubble__offer"
                  onClick={() => {
                    const retry = m.retry;
                    if (retry !== undefined) {
                      setMessages((ms) => ms.filter((x) => x.id !== m.id));
                      void runTurn(retry, false, 0);
                    }
                  }}
                >
                  Try again
                </button>
              ) : null}
            </div>
          </div>
        ))}

        {thinking ? (
          <div className="bubble-row bubble-row--assistant">
            <div
              className="bubble bubble--assistant bubble--thinking"
              aria-label="Froshbot is thinking"
            >
              <span />
              <span />
              <span />
            </div>
          </div>
        ) : null}
      </div>

      {inPayoffMode() && messages.length <= 2 ? (
        <div className="chat__payoff">
          <button
            type="button"
            className="chat__starter"
            disabled={thinking}
            onClick={() => send("Build my study plan")}
          >
            Build my study plan
          </button>
          <button
            type="button"
            className="chat__starter chat__payoff-dm"
            onClick={() => openTelegram("https://t.me/FreshmanAppBot")}
          >
            <i className="fa-brands fa-telegram" aria-hidden="true" /> Say hi in my DMs
          </button>
        </div>
      ) : null}

      <div className="chat__composer">
        <input
          className="field"
          placeholder="Message Froshbot…"
          value={draft}
          disabled={thinking}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") send(draft);
          }}
        />
        <button
          type="button"
          className="chat__send"
          aria-label="Send"
          disabled={thinking || draft.trim() === ""}
          onClick={() => send(draft)}
        >
          <i className="fa-solid fa-arrow-up" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
